import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import moment from 'moment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import TextButton from 'components/Luxkit/Button/TextButton'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Pill from 'components/Luxkit/Pill'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferPageBNBLcta from '../OfferPageBNBLcta'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable/Clickable'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import { max, unique } from 'lib/array/arrayUtils'
import ModalContext from 'contexts/ModalContext'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import TextLink from 'components/Luxkit/TextLink'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import Label from 'components/Luxkit/Label'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { feesToObject } from 'lib/offer/pricing'
import FlashHotelMarginText from './FlashHotelMarginText'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchOfferPackageAddons } from 'actions/AddonActions'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isAgentHubMultiCartItemsEnabled } from 'agentHub/selectors/agentHubSelectors'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import useLoyaltyOfferPageTargetRoomUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetRoomUpgrade'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import ContactContext from 'contexts/contactContext'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  fetching?: boolean;
  currency: string;
  isSpoofed: boolean;
  isStoreMode: boolean;
  defaultDepositAmountPercentage: number;
  isFlightsSelected: boolean;
  userMultiItemCartEnabled: boolean;
}

function FlashHotelOptionPriceDetails(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    fetching,
    currency,
    isSpoofed,
    isStoreMode,
    defaultDepositAmountPercentage,
    isFlightsSelected,
    userMultiItemCartEnabled,
    packageView,
  } = props

  const {
    package: pkg,
    flightsAvailable,
    pricing,
    memberPricing,
    pricingWithFlights,
    memberPricingWithFlights,
  } = packageView
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])

  const fees = feesToObject(pricing.fees)

  const priceWithoutPropertyFees = pricing.price - (fees.property ?? 0)

  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const offerPageDispatch = useOfferPageDispatch()
  const { currentRegionCode } = useContext(GeoContext)
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (flightsAvailable || offer.bundledWithFlightsOnly) && config.businessTraveller.currentAccountMode !== 'business'
  const hasFlights = pricingWithFlights && !offer.flightCacheDisabled
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const extraGuestSurchargesPayableAtProperty = !!pkg.roomRate?.extraGuestSurcharges.length && pkg.roomRate?.extraGuestSurcharges.some(surcharge => surcharge?.payableAtProperty)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = !!pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const discountPercent = offer.bundledWithFlightsOnly ? pricingWithFlights?.discountPercent : max([pricing.discountPercent, pricingWithFlights?.discountPercent ?? 0])

  const contact = useContext(ContactContext)
  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330
  }, [checkInMoment])

  const showPriceDetails = !!fees.taxesAndFees || !!fees.property || config.agentHub.isEnabled
  const showMemberPrice = !!memberPricing && memberPricing.price > 0

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetRoomUpgrade(pkg)
  const showBNBL = !selectedTargetUpgradePackage && packageView.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && config.accommodation.hasBnbl
  const canAddToUserCart = canBuyNow && userMultiItemCartEnabled && !selectedTargetUpgradePackage
  const shouldShowDiscount = !offer.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && (offer.bundledWithFlightsOnly ? hasFlights : true)
  const shouldShowValue = pricing.value > pricing.price && pkg.shouldDisplayValue
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, pricing.price)
  const isIndiaRegion = currentRegionCode === 'IN'
  const isFlexibleCancellation = isFlexibleCancellationPolicy(packageView.package?.roomRate?.cancellationPolicy)

  const roomRateIsReservableForZero = isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const showReserveForZero = (isReserveForZeroEnabled || isSpoofed) && roomRateIsReservableForZero
  const showDepositTag = isDepositEnabled && !isSpoofed
  const showFlexiblePaymentTag = isDepositEnabled && isSpoofed

  const showFlightsDealTag = flightsEnabled && flightOrigin && flightDestination && deals.length > 0

  const isMobileDevice = useIsMobileDevice()
  const showHotelAndFlightsToggle = !isMobileDevice && flightsEnabled && !offer.bundledWithFlightsOnly
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected
  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture

  const showModal = useContext(ModalContext)
  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      const currentPrice = isFlightsSelected ? pricingWithFlights?.price : pricing.price

      if (currentPrice) {
        Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomRate?.id, includesFlights: isFlightsSelected, packageOptionPrice: currentPrice }))
      }
    } else {
      trackEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, isFlightsSelected, offer, pkg.roomRate?.id, pricing.price, pricingWithFlights?.price, trackEvent])

  const onFlexPaymentPolicyOpen = useCallback(async() => {
    showModal(
      <ReserveForZeroAndDepositPolicyModal
        depositAmountPercentage={depositAmountPercentage}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />,
    )
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])
  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])

  const otherFees = {
    propertyFees: packageView.propertyFees,
    extraGuestsSurcharge: extraGuestSurchargesPayableAtProperty ? packageView.extraGuestsSurcharge : 0,
  }

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isSpoofed) {
      dispatch(fetchOfferPackageAddons(offer.id, pkg.id))
    }
  }, [dispatch, offer.id, pkg.id, isSpoofed])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: hideHotelAndFlightsPriceBlock,
    }),
  ], [offer, packageView, hideHotelAndFlightsPriceBlock])

  return <>
    <Group direction="horizontal" gap={8} wrap="wrap" noDisplayWhenEmpty>
      {shouldShowDiscount && !!discountPercent && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={discountPercent}
      />}
      {showDepositTag && <SecureWithDepositTagAndModal
        isFlexibleCancellation={isFlexibleCancellation}
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
      />}
      {showFlexiblePaymentTag && <Clickable onClick={onFlexPaymentPolicyOpen}>
        <Pill startIcon={<LineClockDollarIcon />} kind="tertiary" variant="primary">
          Flexible payment options
        </Pill>
      </Clickable>}
    </Group>
    <AccommodationPackageTilePricingBlocks>
      {!offer.bundledWithFlightsOnly && !hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="pricing"
        title={(flightsEnabled && !showHotelAndFlightsToggle) ? 'Hotel' : undefined}
        callToAction={<Group gap={8} direction="vertical" horizontalAlign="stretch">
          <OfferBookButton
            size="large"
            type="submit"
            data-testid="HotelBookingBtn"
            disabled={fetching}
            packageView={packageView}
            onSubmit={onSubmit}
          >
            {canBuyNow ? 'Book now' : 'Check availability'}
          </OfferBookButton>
          {canAddToUserCart && <OfferAddToCartButton
            size="large"
            type="submit"
            disabled={fetching}
            packageView={packageView}
          />}
          {canBuyNow && isAgentHubMultiCartItemsEnabled && (
            <OfferBookButton
              size="large"
              type="submit"
              disabled={fetching}
              packageView={packageView}
              onSubmit={onSubmit}
              addToCartAndKeepBrowsing
              kind="secondary"
            >
              Add to cart
            </OfferBookButton>
          )}
        </Group>}
      >
        <VerticalSpacer gap={12}>
          <VerticalSpacer gap={4}>
            <div>
              {!isStoreMode && isSpoofed && <FlashHotelMarginText
                checkIn={checkInMoment?.format(ISO_DATE_FORMAT)}
                pkg={pkg}
                type="exact"
              />}

              <PriceRowPriceCaption>
                {pluralizeToString('night', pkg.duration)} {!checkIn && 'from'}
              </PriceRowPriceCaption>

              <LuxPlusPriceStack
                size={!flightsEnabled || showHotelAndFlightsToggle ? 'L' : 'S'}
                price={pricing.price}
                memberPrice={memberPricing?.price}
                saleUnit="total"
                testid="offer-package-price"
                offerConfig={offer.luxPlus}
              />
              <PriceRowTaxesAndFees />
              {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={pkg.roomPolicyDescription}
              />}
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={pricing.value}
              />}
              {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                type="exact"
                offer={offer}
                totalCost={pricing.price}
                numberOfNights={pkg.duration}
                roomCount={rooms?.length}
              />}
            </div>
            <PriceRowAgentHubCommission
              offerId={offer.id}
              size="M"
              productType={offer.productType}
              kind="plain"
              offerTotal={priceWithoutPropertyFees}
              packageId={pkg.id}
            />
            <div>
              {showPriceDetails && <OfferPriceDetailsRow
                trigger="price-row"
                triggerSize="M"
                offerPage
                offer={offer}
                duration={pkg.duration}
                propertyFees={fees.property}
                hotelPrice={priceWithoutPropertyFees}
                otherFees={otherFees}
                rooms={rooms}
                extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
                hotelMemberPrice={packageView.hotelMemberPrice}
                showMemberPrice={showMemberPrice}
                cta={<Group
                  direction="vertical"
                  tabletDirection="horizontal"
                  horizontalAlign="stretch"
                  tabletHorizontalAlign="end"
                  gap={8}
                >
                  {canAddToUserCart && <OfferAddToCartButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                  />}
                  <OfferBookButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                    onSubmit={onSubmit}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </OfferBookButton>
                </Group>}
              />}
              {showReserveForZero && <TextLink
                variant="highlight"
                size="small"
                weight="bold"
                endIcon={<LineInfoCircleIcon colour="primary" />}
                onClick={openReserveForZeroPolicyModal}
              >
                Reserve now for free
              </TextLink>}
            </div>
          </VerticalSpacer>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || (showHotelAndFlightsToggle && isFlightsSelected)) ? 'estimate' : 'exact'}
            modalTitle={packageView.package.roomType.name}
          />
        </VerticalSpacer>
      </AccommodationPackageTilePricingBlock>}

      {flightsEnabled && !hideHotelAndFlightsPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="flights-pricing"
        title={(!flightsBundledNoCache && !showHotelAndFlightsToggle) ? 'Hotel + flights' : undefined}
        callToAction={<>
          {!hasFlights && flightOrigin.code === flightDestination?.code && <TextButton
            size="large"
            data-filter="airport"
            kind="tertiary"
            onClick={onToggleFilter}
          >
            Change airport
          </TextButton>}
          {flightsBookable && (!canBuyNow || !userMultiItemCartEnabled) && <OfferBookButton
            size="large"
            type="submit"
            data-testid="HotelAndFlightsBtn"
            disabled={fetching}
            packageView={packageView}
            data-buytype="flights"
            onSubmit={onSubmit}
          >
            {canBuyNow ? 'Book now' : 'Check availability'}
          </OfferBookButton>}
          {flightsBookable && (canBuyNow && userMultiItemCartEnabled) && <OfferSelectPackageButton
            size="large"
            type="submit"
            disabled={fetching}
            packageView={packageView}
            data-buytype="flights"
          />}
          {flightsBundledNoCache &&
            <Group direction="vertical" gap={12} horizontalAlign="center">
              <BodyText variant="small">OR</BodyText>
              <BodyText variant="medium" weight="bold">
                Call our concierge team for<br />exclusive prices & priority booking
              </BodyText>
              <TextButton
                size="large"
                kind="secondary"
                startIcon={<LinePhoneIcon />}
                href={`tel:${contact.defaultContact.number}`}
                rel="nofollow"
              >
                {contact.defaultContact.humanReadable}
              </TextButton>
            </Group>}
        </>}
      >
        <VerticalSpacer gap={12}>
          <div>
            {!hasFlights && flightOrigin.code === flightDestination?.code && <PriceRowCaption>
              No flights available from {flightOrigin.name}. Please select another departing airport.
            </PriceRowCaption>}
            {!hasFlights && !flightsBundledNoCache && flightsBookable && <PriceRowCaption>
              {!isIndiaRegion && 'Add flights to the hotel package. Select from all major airlines at the next step.'}
              {isIndiaRegion && 'Add flights to your hotel package from the best available airlines.'}
            </PriceRowCaption>}
            {!hasFlights && departureTooFarInFuture && <PriceRowCaption>
              Flights can only be booked up to 11 months in advance.
            </PriceRowCaption>}
            {hasFlights && <>
              {checkIn && <PriceRowPriceCaption>
                {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', pkg.duration)} + flights
              </PriceRowPriceCaption>}
              {!checkIn && <PriceRowPriceCaption>
                {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', pkg.duration)} + flights from
              </PriceRowPriceCaption>}
              <LuxPlusPriceStack
                size="S"
                price={pricingWithFlights.price}
                memberPrice={memberPricingWithFlights?.price}
                saleUnit="total*"
                testid="offer-package-price-with-flight"
                offerConfig={offer.luxPlus}
              />
              {!rooms && <PriceRowCaption>Two Adults</PriceRowCaption>}
              <PriceRowTaxesAndFees />
              {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={pkg.roomPolicyDescription}
              />}
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={pricingWithFlights.value}
              />}
              {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                type="exact"
                offer={offer}
                totalCost={pricing.price}
                numberOfNights={pkg.duration}
                roomCount={rooms?.length}
              />}
              <PriceRowAgentHubCommission
                size="M"
                kind="plain"
                offerId={offer.id}
                productType={offer.productType}
                offerLocations={offerLocations}
                vendorName={vendorName}
                offerTotal={pricingWithFlights.price}
                packageId={pkg.id}
              />
              {showFlightsDealTag && <Label variant="critical" kind="plain" startIcon={<SolidFireIcon />}>Flight deal available</Label>}
              {showPriceDetails && <OfferPriceDetailsRow
                trigger="price-row"
                triggerSize="M"
                offerPage
                offer={offer}
                duration={pkg.duration}
                propertyFees={packageView.propertyFees}
                otherFees={otherFees}
                hotelPrice={packageView.hotelPrice}
                flightPrice={packageView.flightPrice}
                rooms={rooms}
                extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
                hotelMemberPrice={packageView.hotelMemberPrice}
                showMemberPrice={showMemberPrice}
                cta={<Group
                  direction="vertical"
                  tabletDirection="horizontal"
                  horizontalAlign="stretch"
                  tabletHorizontalAlign="end"
                  gap={8}
                >
                  {canAddToUserCart && <OfferSelectPackageButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                    data-buytype="flights"
                  />}
                  {(!userMultiItemCartEnabled || !canBuyNow) && <OfferBookButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                    data-buytype="flights"
                    onSubmit={onSubmit}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </OfferBookButton>
              }
                </Group>}
              />}
            </>}
            <AtolProtectedTooltip />
          </div>
          {hasFlights && <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || (showHotelAndFlightsToggle && isFlightsSelected)) ? 'estimate' : 'exact'}
            modalTitle={packageView.package.roomType.name}
          />}
        </VerticalSpacer>
      </AccommodationPackageTilePricingBlock>}
    </AccommodationPackageTilePricingBlocks>
    <LoadingIndicator visible={fetching} floating opaque />
    {showBNBL && <OfferPageBNBLcta packageView={packageView} />}
  </>
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  isSpoofed: appState.auth.account.isSpoofed,
  isStoreMode: appState.system.storeMode,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isFlightsSelected: state.flights,
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  open: !!state.activeFilter,
}), FlashHotelOptionPriceDetails, preventRenderWithOpenFilter)
